// API utility functions for Bloom IQ backend
import axios from 'axios';
import * as URLs from '../constants/URLs';


import { authApp } from '../firebase';
import { getAuth } from 'firebase/auth';
import { getLogoutRedirect } from './logoutRedirect';
import { withEnglishPorts } from './portNames';
import { normalizePdfLang } from '../constants/importerSeasonPdf.js';
import { toAlertCreateError } from './alertCreateError';

// =============================================================================
// Client-side logging policy
// =============================================================================
// Never log BiQ tool/SSE payloads in production. This can leak internal details
// (including SQL/tool traces) to any user via DevTools Console.
const isDebugLoggingEnabled = () =>
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true';

const debugLog = (...args) => {
  if (isDebugLoggingEnabled()) console.log(...args);
};

const debugWarn = (...args) => {
  if (isDebugLoggingEnabled()) console.warn(...args);
};

// Configure axios to use headers for authentication
// We need to get token from firebase auth and add it to the headers
export const getToken = async () => {
  const auth = getAuth(authApp);
  await auth.authStateReady();
  return auth.currentUser?.getIdToken() ?? null;
};

const getEmail = async () => {
  const auth = getAuth(authApp);
  return auth.currentUser?.email || null;
};

// Add interceptor to axios to add token to headers
axios.interceptors.request.use(async (config) => {
  const auth = getAuth(authApp);
  if (auth.currentUser) {
    const token = await auth.currentUser.getIdToken();
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Helper function to process API responses
const processResponse = (response) => {
  if (response.data && response.data.status === false) {
    throw new Error(response.data.message || response.data.response || 'Request failed');
  }
  return response.data;
};

// User Management APIs
export const getUserDetails = async (email) => {
  try {
    const response = await axios.post(URLs.GET_USER_DETAILS, { email });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.response || error.message || 'Failed to fetch user details');
  }
};

export const updateUserDetails = async (email, name, surname) => {
  try {
    const response = await axios.post(URLs.UPDATE_USER_DETAILS, { email, name, surname });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to update user details');
  }
};

export const logUserActivity = async (email, activity) => {
  try {
    const response = await axios.post(URLs.LOG_USR_ACTIVITY, { email, activity });
    return processResponse(response);
  } catch (error) {
    console.error('Failed to log user activity:', error);
    // Don't throw error for logging failures
    return null;
  }
};

// Public BiQ Conversion Tracking
export const exchangeAuthCode = async (code) => {
  try {
    const response = await axios.post(URLs.EXCHANGE_AUTH_CODE, { code });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to exchange auth code');
  }
};

export const recordLoginConversion = async (conversionRef) => {
  try {
    const response = await axios.post(URLs.RECORD_LOGIN_CONVERSION, conversionRef);
    return processResponse(response);
  } catch (error) {
    console.error('Failed to record login conversion:', error);
    // Non-fatal — don't rethrow, just log
  }
};

// Analytics APIs
export const loadChartData = async (commodity, dateStart = null, dateEnd = null) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity };
    if (dateStart) payload.date_start = dateStart;
    if (dateEnd) payload.date_end = dateEnd;

    const response = await axios.post(URLs.LOAD_CHART_DATA, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load chart data');
  }
};

// Individual Chart APIs
export const loadPriceChart = async (commodity, dateStart = null, dateEnd = null) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity };
    if (dateStart) payload.date_start = dateStart;
    if (dateEnd) payload.date_end = dateEnd;

    const response = await axios.post(URLs.USA_PRICE_CHART, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load price chart data');
  }
};

/**
 * Fetch USA terminal price chart data (low/high prices per district)
 * @param {string} email - User email
 * @param {string} commodity - Commodity name (lowercase)
 * @param {string|null} dateStart - Optional start date (YYYY-MM-DD)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD)
 */
export const loadTerminalPriceChart = async (commodity, dateStart = null, dateEnd = null) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity };
    if (dateStart) payload.date_start = dateStart;
    if (dateEnd) payload.date_end = dateEnd;

    const response = await axios.post(URLs.USA_TERMINAL_PRICE_CHART, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load terminal price chart data');
  }
};

/**
 * Map UI Find label to API find param.
 */
export const mapFindToApi = (uiFind) => {
  const v = String(uiFind || '').toLowerCase();
  if (v === 'high' || v.includes('high')) return 'high';
  if (v === 'low' || v.includes('low')) return 'low';
  return 'avg';
};

export const TERMINAL_PRICE_PULSE_TABLE_CHART_TYPE = 'DATA_terminal_price_pulse_table';

/**
 * Fetch terminal price pulse table rows (weekly deltas + FOB deltas).
 *
 * Backend contract:
 * - POST /terminal_price_pulse_table_endpoint
 * - Auth: Firebase ID token in Authorization header (axios request interceptor)
 * - Body: { commodity, find?, weeks?, iso_week?, iso_year?, email? }
 * - Response chart_type: DATA_terminal_price_pulse_table
 *
 * Send both iso_week and iso_year, or neither (latest week with data).
 * Do not send date_start / date_end (line chart and score cards use those).
 */
export const loadTerminalPricePulseTable = async ({
  email,
  commodity,
  find = 'avg',
  isoWeek = null,
  isoYear = null,
  weeks = 8,
}) => {
  try {
    if (!commodity) {
      throw new Error('Commodity is required');
    }
    const payload = {
      commodity: typeof commodity === 'string' ? commodity.toLowerCase() : commodity,
      find: mapFindToApi(find),
      weeks,
    };
    if (email) payload.email = email;
    if (isoWeek != null && isoYear != null) {
      payload.iso_week = isoWeek;
      payload.iso_year = isoYear;
    }

    const response = await axios.post(URLs.TERMINAL_PRICE_PULSE_TABLE, payload, {
      headers: { 'Accept-Encoding': 'gzip' },
    });
    const data = response?.data;

    if (!data || data.status !== true) {
      throw new Error(
        data?.message || data?.error || 'Failed to load terminal price pulse table data'
      );
    }
    if (data.chart_type && data.chart_type !== TERMINAL_PRICE_PULSE_TABLE_CHART_TYPE) {
      debugWarn('Unexpected terminal price pulse chart_type', data.chart_type);
    }
    return data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401) {
      window.location.assign(getLogoutRedirect());
      throw new Error('Unauthorized');
    }
    if (status === 503) {
      const reason = error?.response?.data?.reason;
      if (reason === 'serving_table_schema_mismatch') {
        throw new Error('Data refreshing… Please try again later.');
      }
    }
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to load terminal price pulse table data'
    );
  }
};

/**
 * Fetch USA daily volume chart data with year-over-year comparison
 * @param {string} email - User email
 * @param {string} commodity - Commodity name (lowercase)
 * @param {string|null} dateStart - Optional start date (YYYY-MM-DD)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD)
 */
export const loadDailyVolumeChart = async (commodity, dateStart = null, dateEnd = null) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity };
    if (dateStart) payload.date_start = dateStart;
    if (dateEnd) payload.date_end = dateEnd;

    const response = await axios.post(URLs.USA_DAILY_VOLUME_CHART, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load daily volume chart data');
  }
};

// =============================================================================
// Preset Analytics View APIs
// =============================================================================

/**
 * Standard preset scorecards (3 cards).
 * Endpoint is expected to be created server-side.
 *
 * Proposed payload/response shape:
 * payload: { email, commodity, date_start?, date_end? }
 * response.data: {
 *   todays_market, todays_market_sub,
 *   vs_last_year, vs_last_year_sub,
 *   vs_last_week, vs_last_week_sub
 * }
 */
export const loadStandardAnalyticsScorecards = async (email, commodity, dateStart = null, dateEnd = null) => {
  try {
    const payload = { email, commodity };
    if (dateStart) payload.date_start = dateStart;
    if (dateEnd) payload.date_end = dateEnd;

    // Backend to implement:
    // - /standard_score_cards_endpoint (recommended)
    // or update this path to whatever you create (e.g. /fob_score_cards_endpoint)
    const base = import.meta.env.VITE_API_BASE_URL;
    const response = await axios.post(`${base}/standard_score_cards_endpoint`, payload);
    return processResponse(response);
  } catch (error) {
    // Fail-open behavior: caller decides what to render. We still throw for visibility.
    throw new Error(error.response?.data?.message || error.message || 'Failed to load standard analytics scorecards');
  }
};

// =============================================================================
// KPI Score Cards (View 1)
// =============================================================================

export const loadFobPricePulseScorecards = async (email, commodity, dateStart = null, dateEnd = null) => {
  try {
    const payload = { email, commodity };
    if (dateStart) payload.date_start = dateStart;
    if (dateEnd) payload.date_end = dateEnd;

    const base = import.meta.env.VITE_API_BASE_URL;
    const response = await axios.post(`${base}/fob_price_pulse_score_cards_endpoint`, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load FOB score cards');
  }
};

export const loadVolumeByOriginScorecards = async (email, commodity, dateStart = null, dateEnd = null) => {
  try {
    const payload = { email, commodity };
    if (dateStart) payload.date_start = dateStart;
    if (dateEnd) payload.date_end = dateEnd;

    const base = import.meta.env.VITE_API_BASE_URL;
    const response = await axios.post(`${base}/volume_by_origin_score_cards_endpoint`, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load Volume score cards');
  }
};

export const loadTerminalPricePulseScorecards = async (email, commodity, dateStart = null, dateEnd = null) => {
  try {
    const payload = { email, commodity };
    if (dateStart) payload.date_start = dateStart;
    if (dateEnd) payload.date_end = dateEnd;

    const base = import.meta.env.VITE_API_BASE_URL;
    const response = await axios.post(`${base}/terminal_price_pulse_score_cards_endpoint`, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load Terminal score cards');
  }
};

/**
 * Fetch USA historic volume chart data by ISO week for season comparison
 * Note: This endpoint does not support date_start/date_end parameters
 * @param {string} email - User email
 * @param {string} commodity - Commodity name (lowercase)
 */
export const loadHistoricVolumeChart = async (commodity) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity };
    const response = await axios.post(URLs.USA_HISTORIC_VOLUME_CHART, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load historic volume chart data');
  }
};

/**
 * Fetch USA predictions chart data (season predictions and progress)
 * @param {string} email - User email
 * @param {string} commodity - Commodity name (lowercase)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD)
 */
export const loadPredictionsChart = async (commodity, dateEnd = null) => {
  try {
    const payload = {
      commodity: typeof commodity === 'string' ? commodity.toLowerCase() : commodity,
    };
    if (dateEnd) payload.date_end = dateEnd;

    const response = await axios.post(URLs.USA_PREDICTIONS_CHART, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load predictions chart data');
  }
};

/**
 * Fetch USA cluster chart data (ML-clustered weekly prices)
 * Note: This endpoint does not support date parameters
 * @param {string} email - User email
 * @param {string} commodity - Commodity name (lowercase)
 */
export const loadClusterChart = async (commodity) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity };
    const response = await axios.post(URLs.USA_CLUSTER_CHART, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load cluster chart data');
  }
};

/**
 * Fetch Smart Price Classifier filter data (raw rows for cascading filters)
 * @param {string} email - User email
 * @param {string} commodity - Commodity name (lowercase)
 */
export const loadPriceClassifierData = async (commodity) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity };
    const response = await axios.post(URLs.PRICE_CLASSIFIER_DATA, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load price classifier data');
  }
};

/**
 * Classify a price across product types using k=5 ML model
 * Returns 201 pre-computed price points (±$10 at $0.10 steps) for instant slider updates
 * @param {string} email - User email
 * @param {string} commodity - Commodity name (lowercase)
 * @param {number} isoWeek - ISO week number
 * @param {number} isoYear - ISO year
 * @param {number} price - Price to classify
 * @param {Object} filters - Filter selections { type, grade, package, variety, item_size, ... }
 */
export const classifyPrice = async (commodity, isoWeek, isoYear, price, filters = {}) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity, iso_week_number: isoWeek, iso_year: isoYear, price, filters };
    const response = await axios.post(URLs.PRICE_CLASSIFIER_CLASSIFY, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to classify price');
  }
};

/**
 * Fetch producing regions map data for a commodity
 * Shows global producing regions with harvest status and varieties
 * @param {string} email - User email
 * @param {string} commodity - Commodity name (lowercase)
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD) to determine harvest status month
 */
export const loadProducingRegions = async (commodity, dateEnd = null) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity: commodity.toLowerCase() };
    if (dateEnd) payload.date_end = dateEnd;

    const response = await axios.post(URLs.PRODUCING_REGIONS_CHART, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load producing regions');
  }
};

// =============================================================================
// FOB Price Dashboard APIs
// =============================================================================

/**
 * Fetch FOB average price data for year-over-year comparison
 * Returns last 5 years of monthly data. Frontend handles filtering.
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const loadFobPriceAvg = async (commodity, years = null) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity: commodity.toLowerCase() };
    if (Array.isArray(years) && years.length > 0) {
      payload.years = years;
    }
    const response = await axios.post(URLs.FOB_PRICE_AVG, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load FOB price average data');
  }
};

/**
 * Fetch FOB price by origin data for heatmap and line charts
 * Returns last 5 years of monthly data. Frontend handles filtering.
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const loadFobPriceByOrigin = async (commodity) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity: commodity.toLowerCase() };
    const response = await axios.post(URLs.FOB_PRICE_BY_ORIGIN, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load FOB price by origin data');
  }
};

/**
 * Fetch seasonality data showing when each origin is in season
 * Returns season availability by month for each origin.
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const loadFobSeasonality = async (commodity) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity: commodity.toLowerCase() };
    const response = await axios.post(URLs.FOB_SEASONALITY, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load seasonality data');
  }
};

// =============================================================================
// Market Share Dashboard APIs
// =============================================================================

/**
 * Fetch market share distribution data for stacked area chart
 * Returns last 5 years of monthly data. Frontend handles filtering.
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const loadMarketShareChart = async (commodity) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity: commodity.toLowerCase() };
    const response = await axios.post(URLs.MARKET_SHARE_CHART, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load market share chart data');
  }
};

/**
 * Fetch market share summary table data with comparisons and trends
 * Returns summary for current period with delta vs last year and 3-yr avg.
 * @param {string} email - User email
 * @param {string} commodity - Commodity name
 */
export const loadMarketShareSummary = async (commodity) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity: commodity.toLowerCase() };
    const response = await axios.post(URLs.MARKET_SHARE_SUMMARY, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load market share summary data');
  }
};

export const getCommodities = async (email) => {
  try {
    const response = await axios.post(URLs.GET_COMMODITIES, { email: await getEmail() });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch commodities');
  }
};

export const getCommoditySkuPrice = async (commodity) => {
  try {
    const response = await axios.post(URLs.GET_COMMODITY_SKU_PRICE, { commodity });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch commodity SKU price');
  }
};

export const getCommoditySkuVolume = async (commodity) => {
  try {
    const response = await axios.post(URLs.GET_COMMODITY_SKU_VOLUME, { commodity });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch commodity SKU volume');
  }
};

// Chat/BiQ APIs
export const talkToBiq = async (email, prompt) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const response = await axios.post(URLs.TALK_TO_BIQ, { prompt });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to talk to BiQ');
  }
};

// BiQ Chat - Send message and get response
export const sendBiqMessage = async (email, message) => {
  try {
    // Placeholder: Will call BIQ endpoint
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const response = await axios.post(URLs.BIQ, { prompt: message });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to send message to BiQ');
  }
};

// =============================================================================
// BiQ Agentic Chat - Streaming SSE Support
// =============================================================================
// Model selection is controlled by backend DEFAULT_MODEL configuration

const COMPLETE_TEXT_FIELDS = ['answer', 'response', 'text'];

function extractCompleteEventText(event) {
  if (!event || typeof event !== 'object') return '';
  for (const key of COMPLETE_TEXT_FIELDS) {
    const v = event[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return '';
}

/**
 * If the model response is the same body twice (common when the stream replays
 * after a full replace, or the complete payload duplicates the stream), collapse
 * to a single copy by finding a split where left.trim() === right.trim().
 */
function collapseOneAdjacentDuplicateRun(s) {
  const n = s.length;
  const MIN_HALF = 20;
  if (n < MIN_HALF * 2) return s;

  const mid = Math.floor(n / 2);
  const window = Math.min(8000, Math.max(100, Math.floor(n / 6)));
  /** @type {Set<number>} */
  const candidates = new Set();
  for (let d = -window; d <= window; d++) {
    const sp = mid + d;
    if (sp >= MIN_HALF && n - sp >= MIN_HALF) candidates.add(sp);
  }
  const searchFrom = Math.max(MIN_HALF, Math.floor(n / 3));
  const searchTo = Math.min(n - MIN_HALF, Math.ceil((2 * n) / 3));
  for (let i = searchFrom; i < searchTo; i++) {
    if (s[i - 1] === '\n' && s[i] === '\n') {
      candidates.add(i + 1);
    }
  }

  for (const split of candidates) {
    if (split < MIN_HALF || n - split < MIN_HALF) continue;
    const left = s.slice(0, split);
    const right = s.slice(split);
    const lt = left.trim();
    const rt = right.trim();
    if (lt.length < MIN_HALF) continue;
    if (lt === rt) {
      return left.trimEnd();
    }
  }
  return s;
}

export function collapseAdjacentDuplicateAnswer(text) {
  if (text == null || text === '') return text;
  let s = String(text).replace(/\r\n/g, '\n');
  for (let i = 0; i < 12; i++) {
    const next = collapseOneAdjacentDuplicateRun(s);
    if (next === s) break;
    s = next;
  }
  return s;
}

/**
 * Some agentic backends send a final `complete` event whose `answer` repeats the
 * text that was already streamed (e.g. answer === streamed + streamed). Collapse
 * to one copy using the in-stream accumulator as ground truth.
 */
export function normalizeCompleteAnswerEvent(event, accumulatedText) {
  if (!event || accumulatedText == null || accumulatedText === '') {
    return event;
  }
  const raw = extractCompleteEventText(event);
  if (raw === '') {
    return event;
  }
  const answer = String(raw);
  const acc = String(accumulatedText);
  if (answer === acc) {
    return event;
  }
  if (answer === acc + acc) {
    return { ...event, answer: acc };
  }
  for (const sep of ['\n\n', '\n']) {
    if (answer === acc + sep + acc) {
      return { ...event, answer: acc };
    }
  }
  const accTrim = acc.trim();
  const answerTrim = answer.trim();
  if (accTrim.length > 0 && answerTrim === accTrim + accTrim) {
    return { ...event, answer: acc };
  }
  for (const sep of ['\n\n', '\n']) {
    if (accTrim.length > 0 && answerTrim === accTrim + sep + accTrim) {
      return { ...event, answer: acc };
    }
  }
  if (acc.length > 0 && answer.startsWith(acc)) {
    const tail = answer.slice(acc.length);
    if (tail === acc || (accTrim.length > 0 && tail.trim() === accTrim)) {
      return { ...event, answer: acc };
    }
  }
  return event;
}

/**
 * Pick final assistant markdown from streamed UI state + complete event, then
 * collapse duplicated full-body payloads (stream and/or complete may repeat).
 */
export function finalizeBiqAssistantContent(streamedBody, completeEvent) {
  const streamed = streamedBody == null ? '' : String(streamedBody);
  const merged = normalizeCompleteAnswerEvent(completeEvent ?? {}, streamed);
  const fromEvent = extractCompleteEventText(merged);
  const picked = fromEvent.trim() !== '' ? String(fromEvent) : streamed;
  return collapseAdjacentDuplicateAnswer(picked);
}

/** UUID for one BiQ chat thread (sent as conversation_id on every /chat request). */
export function createBiqConversationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Send a message to the BiQ Agentic endpoint with streaming support.
 *
 * @param {string} email - User's email address
 * @param {string} message - The user's question/message
 * @param {object} options - Optional configuration
 * @param {string} options.conversationId - Optional conversation ID for history
 * @param {string} [options.cancelToken] - Optional UUID; otherwise one is generated for cooperative POST /chat/cancel
 * @param {function} [options.onCancelToken] - Called with the effective cancel_token before the /chat request
 * @param {function} [options.onConversationTitle] - Called with the `conversation_title` SSE event ({conversation_id, title, title_source: 'auto'}) when the backend generates a title for this turn
 * @param {function} onStatus - Callback for status updates (e.g., "Checking Bloom's data...")
 * @param {function} onTextDelta - Callback for streaming text tokens
 * @param {function} onComplete - Callback when response is complete
 * @param {function} onError - Callback for errors (receives generic user-friendly message)
 * @param {AbortSignal} abortSignal - Optional abort signal to cancel request
 *
 * @example
 * await sendBiqAgenticMessage(
 *   email,
 *   "What are strawberry prices?",
 *   { conversationId: '123' },
 *   (status) => console.log('Status:', status.message),
 *   (delta) => setResponse(prev => prev + delta),
 *   (result) => console.log('Complete:', result.answer),
 *   (error) => console.error('Error:', error.message),
 *   abortController.signal
 * );
 */
export const sendBiqAgenticMessage = async (
  email,
  message,
  options = {},
  onStatus,
  onTextDelta,
  onComplete,
  onChartReady,
  onPdfReady,
  onError,
  abortSignal
) => {
  debugLog('[BiQ Agentic] sendBiqAgenticMessage called');
  debugLog('[BiQ Agentic] URL:', URLs.BIQ_AGENTIC);

  try {
    const token = await getToken();
    debugLog('[BiQ Agentic] Token obtained, making request...');

    const effectiveCancelToken =
      typeof options.cancelToken === 'string' && options.cancelToken.length > 0
        ? options.cancelToken
        : typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

    options.onCancelToken?.(effectiveCancelToken);

    const response = await fetch(URLs.BIQ_AGENTIC, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        message,
        source: "webapp",
        conversation_id: options.conversationId,
        cancel_token: effectiveCancelToken,
        deep_thinking: options.deepThinking || false,
        market_trends: options.marketTrends || false,
        // Passed by the caller (e.g. BiQ.jsx) from i18n.language so the
        // backend prompt can default to the user's UI language. The
        // webapp BiQ service (container_chat_app) primarily reads language
        // from Firestore, but including it here means fresh logins respond
        // in the correct language even before UserContext has reconciled.
        ...(options.language ? { language: options.language } : {}),
        ...(options.pageContext
          ? { page_context: options.pageContext }
          : {}),
        ...(options.editOfMessageId
          ? { edit_of_message_id: options.editOfMessageId }
          : {}),
        ...(options.regenerateOfMessageId
          ? { regenerate_of_message_id: options.regenerateOfMessageId }
          : {}),
        ...(options.fileId ? { file_id: options.fileId } : {}),
      }),
      // Auth is the Bearer token above, not cookies. Omitting credentials keeps
      // the request non-credentialed so the cross-origin container-chat-app
      // response (Access-Control-Allow-Origin: *) is accepted in production.
      signal: abortSignal,
    });

    debugLog('[BiQ Agentic] Response status:', response.status);
    debugLog('[BiQ Agentic] Response ok:', response.ok);

    if (!response.ok) {
      // Avoid leaking backend debug payloads (e.g. SQL/tool traces) into console or error messages.
      let errorText = '';
      try {
        errorText = await response.text();
      } catch {
        // ignore
      }
      debugWarn('[BiQ Agentic] Request failed body (truncated):', String(errorText).slice(0, 500));
      throw new Error(`Request failed: ${response.status}`);
    }

    debugLog('[BiQ Agentic] Response OK, starting to read SSE stream...');

    // Read the SSE stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedText = '';
    let completeEventReceived = false;
    let pendingCompleteEvent = null;
    let lastChartData = null;
    let streamConversationId = options.conversationId || null;
    let currentIteration = 0; // Track iteration for preamble replacement
    // After a full `text_replace`, some backends re-emit the same body as `text_delta` tokens.
    let suppressTextDeltaAfterFullReplace = false;
    const SUPPRESS_DELTAS_AFTER_REPLACE_MIN_LEN = 280;

    debugLog('[SSE] Starting to read stream from:', URLs.BIQ_AGENTIC);

    // Buffer for incomplete SSE data that spans multiple network chunks
    let buffer = '';

    // Helper function to process a complete SSE event
    const rememberConversationId = (event) => {
      const cid = event?.conversation_id;
      if (typeof cid === 'string' && cid.trim()) {
        streamConversationId = cid.trim();
      }
    };

    const emitComplete = (event) => {
      rememberConversationId(event);
      const payload = {
        ...event,
        ...(streamConversationId ? { conversation_id: streamConversationId } : {}),
      };
      if (onComplete) {
        onComplete(normalizeCompleteAnswerEvent(payload, accumulatedText));
      }
    };

    const processEvent = (event) => {
      rememberConversationId(event);
      switch (event.type) {
        case 'status':
          if (event.cancel_token) {
            options.onCancelToken?.(event.cancel_token);
          }
          if (onStatus) onStatus(event);
          break;

        case 'tool_start':
        case 'tool_result':
          suppressTextDeltaAfterFullReplace = false;
          if (onStatus) onStatus(event);
          // Capture chart data from tool results
          if (event.chart_data) {
            lastChartData = event.chart_data;
          }
          break;

        case 'text_delta':
          // Public BiQ now emits incremental deltas for the final answer:
          // { type: "text_delta", delta: "..." } many times.
          // Keep backwards-compat with older emitters that used `text` instead of `delta`.
          // Also keep iteration-based reset for older agentic mode behavior.
          if (event.iteration && event.iteration > currentIteration) {
            suppressTextDeltaAfterFullReplace = false;
            currentIteration = event.iteration;
            const resetText = event.delta ?? event.text ?? '';
            accumulatedText = resetText;
            if (onTextDelta) onTextDelta(resetText, true);
            break;
          }

          if (suppressTextDeltaAfterFullReplace) {
            break;
          }

          {
            const delta = event.delta ?? event.text ?? '';
            if (typeof delta === 'string' && delta.length > 0) {
              accumulatedText += delta;
              if (onTextDelta) onTextDelta(delta, false);
            }
          }
          break;

        case 'text_replace':
          // Final response from backend — replaces any preamble/accumulated text
          // Backend sends: { type: "text_replace", text: "<full final answer>" }
          accumulatedText = event.text ?? '';
          if (onTextDelta) onTextDelta(accumulatedText, true);
          suppressTextDeltaAfterFullReplace =
            accumulatedText.length >= SUPPRESS_DELTAS_AFTER_REPLACE_MIN_LEN;
          break;

        case 'cancelled':
          pendingCompleteEvent = { ...event, type: 'cancelled' };
          suppressTextDeltaAfterFullReplace = false;
          break;

        case 'done':
          rememberConversationId(event);
          if (!completeEventReceived) {
            completeEventReceived = true;
            suppressTextDeltaAfterFullReplace = false;
            emitComplete({
              ...(pendingCompleteEvent || {}),
              ...event,
              type: pendingCompleteEvent?.type || event.type || 'complete',
            });
            pendingCompleteEvent = null;
          }
          break;

        case 'complete':
          pendingCompleteEvent = event;
          suppressTextDeltaAfterFullReplace = false;
          break;

        case 'chart_ready':
          // Handle chart_ready event for immediate chart rendering
          if (event.chart_data) {
            lastChartData = event.chart_data;
            if (onChartReady) {
              onChartReady(event.chart_data);
            }
          }
          break;

        case 'conversation_title':
          // Emitted once per turn that actually generates a title (server
          // title_source was null at turn start). Always overwrites any
          // local placeholder — see BiqChatHistoryContext.applyServerTitle.
          if (event.conversation_id && event.title) {
            options.onConversationTitle?.(event);
          }
          break;

        case 'pdf_ready':
          if (onPdfReady) {
            onPdfReady({
              pdf_id: event.pdf_id || event.payload?.pdf_id,
              endpoint: event.endpoint,
              filename: event.filename || event.payload?.filename,
              payload: event.payload,
            });
          }
          break;

        // Context guardrails (persistent-sessions plan, Phase 3 section A —
        // see context_guardrails.py's context_status_event/context_full_event).
        // Passed through via `options` like onConversationTitle above, not as
        // a new positional param, so existing callers are unaffected.
        case 'context_status':
          // { level: 'ok'|'warning'|'urgent', projected_tokens, ceiling }.
          // Attached to every normal (non-full) response.
          options.onContextStatus?.(event);
          break;

        case 'context_full':
          // Hard stop: the turn was refused before any model call and the
          // user's message was never stored server-side. `done` still
          // follows this event, so completeEventReceived below still fires
          // onComplete — the caller must treat a msgIndex miss there as
          // expected once it has rolled its optimistic state back.
          options.onContextFull?.(event);
          break;

        case 'error':
          if (onError) onError(event);
          break;
      }
    };

    // Helper to check if JSON is complete (balanced braces)
    const isCompleteJson = (str) => {
      let braceCount = 0;
      let inString = false;
      let escape = false;

      for (const char of str) {
        if (escape) {
          escape = false;
          continue;
        }
        if (char === '\\') {
          escape = true;
          continue;
        }
        if (char === '"') {
          inString = !inString;
          continue;
        }
        if (!inString) {
          if (char === '{') braceCount++;
          else if (char === '}') braceCount--;
        }
      }
      return braceCount === 0;
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        debugLog('[SSE] Stream ended (done=true)');
        break;
      }

      const chunk = decoder.decode(value);

      // Append chunk to buffer
      buffer += chunk;
      // Normalize CRLF so `\r\n\r\n` event boundaries split the same as `\n\n`
      if (buffer.includes('\r')) {
        buffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      }

      // Process complete SSE events from buffer
      // SSE format: "data: {json}\n\n" - events are separated by double newlines
      while (buffer.includes('\n\n')) {
        const eventEndIndex = buffer.indexOf('\n\n');
        const eventStr = buffer.substring(0, eventEndIndex);
        buffer = buffer.substring(eventEndIndex + 2);

        // Process each line in the event (SSE can have multi-line data)
        const lines = eventStr.split('\n');
        let jsonData = '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            jsonData += line.slice(6);
          } else if (line.startsWith('data:')) {
            // Handle "data:" without space
            jsonData += line.slice(5);
          } else if (jsonData && !line.startsWith(':')) {
            // Continuation of previous data line (shouldn't happen in standard SSE)
            jsonData += line;
          }
        }

        if (jsonData) {
          const trimmed = jsonData.trim();
          // Some SSE emitters end with `data: [DONE]`
          if (trimmed === '[DONE]') {
            // Stream terminator only — conversation_id arrives on the prior `done` JSON event.
            continue;
          }
          try {
            const event = JSON.parse(jsonData);
            processEvent(event);
          } catch (parseError) {
            debugWarn('[SSE] JSON parse error:', parseError?.message || String(parseError));
          }
        }
      }

      // Check if we have a partial event that might have complete JSON
      // This handles the case where we have "data: {...}" without the trailing \n\n yet
      if (buffer.startsWith('data: ') || buffer.startsWith('data:')) {
        const dataStart = buffer.startsWith('data: ') ? 6 : 5;
        const jsonPart = buffer.substring(dataStart);

        if (jsonPart.trim() === '[DONE]') {
          buffer = '';
          continue;
        }

        // If we have what looks like complete JSON, try to parse it
        if (jsonPart.trim().endsWith('}') && isCompleteJson(jsonPart)) {
          try {
            const event = JSON.parse(jsonPart);
            debugLog('[SSE] Parsed complete JSON from partial buffer');
            processEvent(event);
            buffer = ''; // Clear the buffer since we consumed it
          } catch {
            // JSON not actually complete, wait for more data
          }
        }
      }
    }

    // If stream ended without a terminal `done` event, call onComplete anyway
    if (!completeEventReceived && onComplete) {
      emitComplete({
        ...(pendingCompleteEvent || {}),
        type: pendingCompleteEvent?.type || 'complete',
        answer: accumulatedText,
        render_chart: !!lastChartData,
        chart_data: lastChartData,
      });
      pendingCompleteEvent = null;
    }

    return {
      success: true,
      response: accumulatedText,
      conversationId: streamConversationId,
    };
  } catch (error) {
    if (error.name === 'AbortError') {
      debugLog('BiQ Agentic request was cancelled');
      return { success: false, cancelled: true };
    }

    console.error('BiQ Agentic error:', error);
    if (onError) {
      onError({
        type: 'error',
        message: 'I encountered an issue processing your request. Please try again.',
      });
    }
    throw error;
  }
};

/** Resolve paths on the same host as POST /chat (container-chat-app, not VITE_API_BASE_URL). */
export const resolveBiqServiceUrl = (path) => {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;
  const base = URLs.getBiqAgenticServiceBase();
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
};

/** POST /chat/upload — GCS store, returns { file_id, filename, ... }. */
export const uploadBiqUserFile = async (file, abortSignal) => {
  const token = await getToken();
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(resolveBiqServiceUrl('/chat/upload'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
    signal: abortSignal,
  });
  if (!response.ok) {
    let message = 'Could not upload the file.';
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch {
      /* keep default */
    }
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

/** POST /chat/cancel on the same host as {@link URLs.BIQ_AGENTIC}. */
const getBiqAgenticCancelUrl = () => {
  if (typeof URLs.BIQ_AGENTIC === 'string' && URLs.BIQ_AGENTIC.startsWith('/')) {
    return URLs.BIQ_AGENTIC.replace(/\/$/, '').replace(/\/chat$/, '/chat/cancel');
  }

  try {
    const u = new URL(URLs.BIQ_AGENTIC);
    const basePath = u.pathname.replace(/\/$/, '');
    if (basePath.endsWith('/chat')) {
      u.pathname = `${basePath}/cancel`;
    } else {
      u.pathname = '/chat/cancel';
    }
    return u.toString();
  } catch {
    return resolveBiqServiceUrl('/chat/cancel');
  }
};

/**
 * Cooperative cancel for an in-flight BiQ agentic stream (same Cloud Run instance as POST /chat).
 * @param {string} cancelToken - Same UUID as POST /chat `cancel_token`.
 * @returns {Promise<{ ok: boolean, signalled: boolean } | null>} Parsed JSON or null on failure.
 */
export const signalBiqAgenticCancel = async (cancelToken) => {
  const url = getBiqAgenticCancelUrl();
  if (!url || !cancelToken) return null;
  const authToken = await getToken();
  if (!authToken) return null;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ cancel_token: cancelToken }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

/**
 * Best-effort cache-warm for a RESUMED long session (persistent-sessions
 * plan, Phase 3 section C / Phase 4b - POST /sessions/<id>/prewarm on the
 * same container-chat-app host as POST /chat; see resolveBiqServiceUrl).
 * Fired by BiqChatHistoryContext.selectChat whenever the user reopens an
 * existing session from the sidebar - never for a brand-new chat, since
 * `startNewChat` never routes through selectChat.
 *
 * Fire-and-forget by contract: callers never await this and never branch on
 * its result. It swallows every failure (missing auth, network error,
 * non-2xx) rather than throwing, and the backend itself no-ops (204) for a
 * session under the prewarm token threshold or with no session doc yet - so
 * this is safe to call unconditionally on every resume.
 *
 * @param {string} conversationId
 * @returns {Promise<void>}
 */
export const prewarmBiqSession = async (conversationId) => {
  if (!conversationId) return;
  try {
    const token = await getToken();
    if (!token) return;
    const url = resolveBiqServiceUrl(`/sessions/${encodeURIComponent(conversationId)}/prewarm`);
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch {
    // Latency optimization only, never a correctness requirement - ignore.
  }
};

// One in-flight prefetch per page+commodity+language+return. Speak with BiQ never calls this.
let _popupGreeting = { key: '', greeting: '', promise: null };
const _seenPages = new Set();
let _lastPage = '';

/** Prefetch POST /popup-greeting. Empty string on any failure (bubble keeps i18n). */
export const prefetchBiqPopupGreeting = async ({
  pageLabel,
  pageId,
  openCommodity,
  language,
  pageFacts,
} = {}) => {
  const page = pageLabel || '';
  const returning = Boolean(page && _lastPage && _lastPage !== page && _seenPages.has(page));
  if (page) {
    _seenPages.add(page);
    _lastPage = page;
  }
  const factsKey = pageFacts ? JSON.stringify(pageFacts) : '';
  const key = `${pageId || ''}|${page}|${openCommodity || ''}|${language || ''}|${returning}|${factsKey}`;
  if (_popupGreeting.key === key && _popupGreeting.greeting) {
    return _popupGreeting.greeting;
  }
  if (_popupGreeting.key === key && _popupGreeting.promise) {
    return _popupGreeting.promise;
  }
  const promise = (async () => {
    try {
      const token = await getToken();
      if (!token) return '';
      const res = await fetch(resolveBiqServiceUrl('/popup-greeting'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          page_id: pageId || '',
          page_label: pageLabel || 'this page',
          open_commodity: openCommodity || '',
          language: language || 'en',
          local_time: new Date().toISOString(),
          returning_to_page: returning,
          ...(pageFacts && typeof pageFacts === 'object' ? { page_facts: pageFacts } : {}),
        }),
      });
      if (!res.ok) return '';
      const data = await res.json();
      const greeting = typeof data?.greeting === 'string' ? data.greeting.trim() : '';
      if (_popupGreeting.key === key) _popupGreeting.greeting = greeting;
      return greeting;
    } catch {
      return '';
    } finally {
      if (_popupGreeting.promise === promise) _popupGreeting.promise = null;
    }
  })();
  _popupGreeting = { key, greeting: '', promise };
  return promise;
};

export const peekBiqPopupGreeting = () => _popupGreeting.greeting || '';

/** Persist the hello as this popup session's first assistant message (open only). */
export const commitBiqPopupGreeting = async ({
  conversationId,
  greeting,
  pageLabel,
} = {}) => {
  if (!conversationId || !greeting) return;
  try {
    const token = await getToken();
    if (!token) return;
    await fetch(resolveBiqServiceUrl('/popup-greeting'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        commit: true,
        conversation_id: conversationId,
        greeting,
        page_label: pageLabel || '',
      }),
    });
  } catch {
    // Persist is best-effort; the bubble already shows the text.
  }
};

export const getLatestPdfReport = async (conversationId) => {
  if (!conversationId) return null;
  const url = resolveBiqServiceUrl(
    `/report/pdf/latest?conversation_id=${encodeURIComponent(conversationId)}`
  );

  const token = await getToken();
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch latest PDF');
  return await res.json();
};

/**
 * Record user interaction on a BiQ turn (feedback Cloud Function).
 *
 * @param {string} chatId - UUID from the SSE `done` event for this answer
 * @param {'copy'|'feedback'|'regenerate'|'share'} action
 * @param {string|null|undefined} userFeedback - required when action is `feedback`
 * @returns {Promise<{ok:boolean,updated:boolean,event_logged:boolean,retrying:boolean,rows_affected:number,event_error?:unknown}|null>}
 */
export const sendBiqResponseFeedback = async (chatId, action, userFeedback = undefined) => {
  if (!chatId) return null;
  const token = await getToken();
  const body = {
    chat_id: chatId,
    action,
    platform: 'webapp',
  };
  if (action === 'feedback') {
    body.user_feedback = userFeedback ?? null;
  }

  const res = await fetch(URLs.BIQ_FEEDBACK_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (!res.ok) {
    let detail = '';
    try {
      const data = await res.json();
      detail = data?.error || '';
    } catch {
      // ignore
    }
    throw new Error(detail || `Feedback request failed: ${res.status}`);
  }

  return res.json();
};

export const downloadPdfReport = async (pdfDownload) => {
  const url = resolveBiqServiceUrl('/report/pdf');
  const token = await getToken();

  const pdfId = pdfDownload?.pdf_id || pdfDownload?.payload?.pdf_id;
  const filename = pdfDownload?.filename || pdfDownload?.payload?.filename;
  const body = pdfId
    ? { pdf_id: pdfId, filename }
    : (pdfDownload?.payload ?? {});

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('PDF download failed');
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename || 'BiQ_Report.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
};

// BiQ streaming function - returns EventSource for Server-Sent Events
export const streamBiqResponse = (email, prompt, onMessage, onError) => {
  // This legacy helper places `email` and `prompt` in the URL query string.
  // In production this increases the chance of sensitive data leaking to logs/telemetry.
  // Keep it DEV-only; use `sendBiqAgenticMessage` instead.
  if (!import.meta.env.DEV) {
    throw new Error('streamBiqResponse is disabled in production. Use sendBiqAgenticMessage instead.');
  }
  const url = `${URLs.BIQ}?email=${encodeURIComponent(email)}&prompt=${encodeURIComponent(prompt)}&stream=true`;
  const eventSource = new EventSource(url);

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage(data);

      // Close connection if done
      if (data.done) {
        eventSource.close();
      }
    } catch (error) {
      console.error('Error parsing SSE data:', error);
    }
  };

  eventSource.onerror = (error) => {
    console.error('SSE Error:', error);
    eventSource.close();
    if (onError) onError(error);
  };

  return eventSource;
};

export const markAsOffensive = async (email, question, answer) => {
  try {
    const response = await axios.post(URLs.MARK_AS_OFFENSIVE, { email, question, answer });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to mark as offensive');
  }
};

export const generateOneClickReport = async (email, commodity = null, dateStart = null, dateEnd = null) => {
  try {
    const payload = { email };
    if (commodity) payload.commodity = commodity;
    if (dateStart) payload.date_start = dateStart;
    if (dateEnd) payload.date_end = dateEnd;

    const response = await axios.post(URLs.ONE_CLICK_REPORT, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to generate one-click report');
  }
};

// Streaming One-Click Report - returns response with streaming simulation
export const streamOneClickReport = async (email, commodity, onChunk, onComplete, onError, abortSignal) => {
  try {
    const token = await getToken();
    
    const response = await fetch(URLs.ONE_CLICK_REPORT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ email, commodity }),
      signal: abortSignal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Request failed: ${response.status} - ${errorText}`);
    }

    // Read the entire response
    const responseText = await response.text();
    
    // Process the response text line by line
    const lines = responseText.split('\n').filter(line => line.trim());
    
    let accumulatedText = '';
    const allChunks = [];

    for (const line of lines) {
      try {
        const parsed = JSON.parse(line);
        
        // Handle chunk format (chunk: "...")
        if (parsed.chunk) {
          allChunks.push(parsed.chunk);
          accumulatedText += parsed.chunk;
        }
        // Handle full response format (answer.response)
        else if (parsed.answer && parsed.answer.response) {
          const formattedReports = parsed.answer.response.split('\n');
          formattedReports.forEach(report => {
            const words = report.split(' ');
            for (let i = 0; i < words.length; i++) {
              const chunk = words[i];
              allChunks.push(chunk);
              accumulatedText += ' ' + chunk;
            }
            allChunks.push('\n');
            accumulatedText += '\n';
          });
        }
        // Handle error
        else if (parsed.error) {
          throw new Error(parsed.error);
        }
      } catch (parseError) {
        // Skip JSON parse errors for incomplete lines
        if (parseError.message && !parseError.message.includes('JSON')) {
          throw parseError;
        }
        continue;
      }
    }

    if (allChunks.length === 0) {
      throw new Error('No response content received');
    }

    // Simulate streaming by revealing content progressively
    let currentText = '';
    for (let i = 0; i < allChunks.length; i++) {
      if (abortSignal?.aborted) {
        break;
      }
      
      currentText = allChunks.slice(0, i + 1).join(' ');
      onChunk(currentText);
      
      // Add delay for streaming effect
      await new Promise(resolve => setTimeout(resolve, 20));
    }

    onComplete(accumulatedText.trim());
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Request was cancelled');
      return;
    }
    console.error('Streaming error:', error);
    onError(error);
  }
};

// Alert APIs - Default (Smart) Alerts
export const getDefaultAlerts = async (email) => {
  try {
    const response = await axios.post(URLs.GET_DEFAULT_ALERTS, { user_email: email });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch default alerts');
  }
};

export const createDefaultAlert = async (alertData) => {
  try {
    const response = await axios.post(URLs.CREATE_DEFAULT_ALERT, alertData);
    return processResponse(response);
  } catch (error) {
    throw toAlertCreateError(error, 'Failed to create default alert');
  }
};

export const updateDefaultAlert = async (email, alert) => {
  try {
    const response = await axios.post(URLs.UPDATE_DEFAULT_ALERTS, {
      user_email: email,
      alert_id: alert.alert_id,
      commodity: alert.commodity,
      enabled: !alert.enabled, // Toggle the enabled state
      created_at: alert.created_at,
    });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to update default alert');
  }
};

export const deleteDefaultAlert = async (email, alert) => {
  try {
    const response = await axios.post(URLs.DELETE_DEFAULT_ALERT, {
      user_email: email,
      alert_id: alert.alert_id,
      commodity: alert.commodity,
      created_at: alert.created_at,
    });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to delete default alert');
  }
};

// Alert APIs - Custom Alerts
export const getCustomAlerts = async (email) => {
  try {
    const response = await axios.post(URLs.GET_CUSTOM_ALERTS, { user_email: email });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch custom alerts');
  }
};

export const createCustomAlert = async (alertData) => {
  try {
    const response = await axios.post(URLs.CREATE_CUSTOM_ALERT, alertData);
    return processResponse(response);
  } catch (error) {
    throw toAlertCreateError(error, 'Failed to create custom alert');
  }
};

export const updateCustomAlert = async (email, alert) => {
  try {
    const response = await axios.post(URLs.UPDATE_CUSTOM_ALERT, {
      user_email: email,
      alert_id: alert.alert_id,
      commodity: alert.commodity,
      enabled: !alert.enabled, // Toggle the enabled state
      created_at: alert.created_at,
    });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to update custom alert');
  }
};

export const deleteCustomAlert = async (email, alert) => {
  try {
    const response = await axios.post(URLs.DELETE_CUSTOM_ALERT, {
      user_email: email,
      alert_id: alert.alert_id,
      commodity: alert.commodity,
      created_at: alert.created_at,
    });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to delete custom alert');
  }
};

// Alert Notifications
export const getDefaultAlertsNotifications = async (email, weeks, language = 'en') => {
  try {
    // Return empty alerts if no weeks provided
    if (!weeks || weeks.length === 0) {
      return { alerts: [] };
    }
    const response = await axios.post(URLs.GET_DEFAULT_ALERTS_NOTIFICATIONS, {
      user_email: email,
      week: weeks,
      language,
    });
    return {
      alerts: response.data?.alerts || [],
    };
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch default alerts notifications');
  }
};

export const getCustomAlertsNotifications = async (email, weeks, language = 'en') => {
  try {
    // Return empty alerts if no weeks provided
    if (!weeks || weeks.length === 0) {
      return { alerts: [] };
    }
    const response = await axios.post(URLs.GET_CUSTOM_ALERTS_NOTIFICATIONS, {
      user_email: email,
      week: weeks,
      language,
    });
    return {
      alerts: response.data?.alerts || [],
    };
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch custom alerts notifications');
  }
};

// Regions and Weather
export const getRegions = async (alertType, commodity, defaultOrCustom = 'default') => {
  try {
    const response = await axios.post(URLs.GET_REGIONS_BY_ALERT_TYPE_AND_COMMODITY, {
      alert_type: alertType,
      commodity,
      default_or_custom: defaultOrCustom,
    });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch regions');
  }
};

export const getWeatherRegions = async (email) => {
  try {
    const response = await axios.post(URLs.GET_WEATHER_REGIONS, { email });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch weather regions');
  }
};

// Authentication APIs
export const registerUser = async (email, password, name, surname, language) => {
  try {
    // Use existing ADD_USER_URL endpoint.
    // `language` is optional and, when present, is persisted on the initial
    // Firestore user doc by add_user_db. This avoids a window between
    // create and first setLanguage call where the doc has no language field.
    const payload = { email, name, surname };
    if (language) payload.language = language;
    const response = await axios.post(URLs.REGISTER_USER, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to register user');
  }
};

export const sendVerificationEmail = async (email) => {
  try {
    const response = await axios.post(URLs.SEND_VERIFICATION_EMAIL, { email });
    return processResponse(response);
  } catch (error) {
    throw new Error(
      error.response?.data?.message || error.message || 'Failed to send verification email'
    );
  }
};

export const verifyEmailCode = async (email, code) => {
  try {
    const response = await axios.post(URLs.VERIFY_EMAIL_CODE, { email, code });
    return processResponse(response);
  } catch (error) {
    throw new Error(
      error.response?.data?.message || error.message || 'Failed to verify email code'
    );
  }
};

export const sendPasswordResetEmail = async (email) => {
  try {
    const response = await axios.post(URLs.SEND_PASSWORD_RESET, { email });
    return processResponse(response);
  } catch (error) {
    throw new Error(
      error.response?.data?.message || error.message || 'Failed to send password reset email'
    );
  }
};

// Language Preferences
export const getLanguage = async () => {
  try {
    const response = await axios.get(URLs.GET_LANGUAGE);
    return response.data;
  } catch (error) {
    // Return default language if fetch fails
    console.error('Failed to fetch language preference:', error);
    return { status: true, language: 'en' };
  }
};

export const setLanguage = async (language) => {
  try {
    const response = await axios.post(URLs.SET_LANGUAGE, { language });
    return processResponse(response);
  } catch (error) {
    throw new Error(
      error.response?.data?.message || error.message || 'Failed to save language preference'
    );
  }
};

export const getSubscriptionDetails = async (email) => {
  try {
    const response = await axios.post(URLs.GET_SUBSCRIPTION_DETAILS, { email });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch subscription');
  }
};

export const getTierLimits = async () => {
  try {
    const response = await axios.get(URLs.GET_TIER_LIMITS);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch tier limits');
  }
};

export const getPaymentMethod = async (email) => {
  try {
    const response = await axios.post(URLs.GET_PAYMENT_METHOD, { email });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch payment method');
  }
};

export const getPaymentMethodPortal = async (email) => {
  try {
    const response = await axios.post(URLs.GET_PAYMENT_METHOD_PORTAL, { email });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to open payment method portal');
  }
};

export const listReceipts = async (email, page = 1, pageSize = 10) => {
  try {
    const response = await axios.post(URLs.LIST_RECEIPTS, { 
      email, 
      page, 
      page_size: pageSize 
    });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch receipts');
  }
};

export const downloadInvoiceFile = async (invoiceId, kind = 'receipt') => {
  let response;
  try {
    response = await axios.post(
      URLs.GET_INVOICE_FILE,
      { invoice_id: invoiceId, kind, as_url: true },
      { responseType: 'blob' },
    );
  } catch (error) {
    // With responseType 'blob' an error body arrives as a Blob; read its message.
    const body = error.response?.data;
    let message = null;
    if (body instanceof Blob) {
      try {
        message = JSON.parse(await body.text()).message;
      } catch {
        message = null; // gateway HTML or plain-text error body
      }
    }
    throw new Error(message || error.message || 'Failed to download document');
  }
  const a = document.createElement('a');
  let objectUrl = null;
  if (response.data.type.includes('json')) {
    // The document is stored: the browser downloads it straight from storage.
    a.href = JSON.parse(await response.data.text()).url;
    a.rel = 'noopener noreferrer';
  } else {
    objectUrl = URL.createObjectURL(response.data);
    a.href = objectUrl;
    a.download = `${kind}.pdf`;
  }
  document.body.appendChild(a);
  a.click();
  a.remove();
  if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
};

export const getReceipt = async (transactionId) => {
  try {
    const response = await axios.post(URLs.GET_RECEIPT, { transaction_id: transactionId });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch receipt');
  }
};

export const validatePromoCode = async (email, code, language) => {
  try {
    const response = await axios.post(URLs.VALIDATE_PROMO_CODE, { email, code, language });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to validate code');
  }
};

export const applyPromoCode = async (email, code, language) => {
  try {
    const response = await axios.post(URLs.APPLY_PROMO_CODE, { email, code, language });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to apply code');
  }
};

// Records, BEFORE checkout, that a promotional user is about to pay for another plan. The
// server ends the promo itself once RevenueCat confirms the payment; the browser never does.
// `useExistingPlan` = "downgrade to the plan I already pay for": ends the promo now, charges nothing.
export const beginPromoSwitch = async (tier, productId, { useExistingPlan = false } = {}) => {
  try {
    const body = { tier };
    if (productId) body.product_id = productId;
    if (useExistingPlan) body.use_existing_plan = true;
    const response = await axios.post(URLs.BEGIN_PROMO_SWITCH, body);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to start plan switch');
  }
};

// Best-effort: seed RevenueCat $email to the signed-in account email before checkout.
// Auth is the Bearer token; never send email in the body. Failures must not block purchase.
export const prepareCheckout = async () => {
  try {
    const response = await axios.post(URLs.PREPARE_CHECKOUT, {});
    return processResponse(response);
  } catch {
    return null;
  }
};

// `target: 'promo'` is kept for older clients; the Paywall no longer uses it.
export const cancelSubscription = async (email, target) => {
  try {
    const response = await axios.post(URLs.CANCEL_SUBSCRIPTION, target ? { email, target } : { email });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to cancel subscription');
  }
};

/** Plan usage for Settings → Usage (Bearer token, no body). */
export const getUsage = async () => {
  try {
    const response = await axios.post(URLs.GET_USAGE, {});
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to get usage');
  }
};

/**
 * In-flight share for getUsageStats. The response is the whole account's
 * usage map, but every useUsageTracking instance asks for it separately -
 * opening BiQ mounts three of them (UsageGuard, plus the page's BIQ_QUERY
 * and BIQ_DEEP_THINKING hooks), which fired three identical POSTs in the
 * same tick.
 */
let usageStatsPending = null;
let usageStatsPendingEmail = null;

export const getUsageStats = async (email) => {
  if (usageStatsPending && usageStatsPendingEmail === email) return usageStatsPending;

  usageStatsPendingEmail = email;
  usageStatsPending = (async () => {
    try {
      const response = await axios.post(URLs.GET_USAGE_STATS, { email });
      return processResponse(response);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to get usage stats');
    } finally {
      usageStatsPending = null;
      usageStatsPendingEmail = null;
    }
  })();

  return usageStatsPending;
};

export const getTokenPackages = async () => {
  try {
    const response = await axios.get(URLs.GET_TOKEN_PACKAGES);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch token packages');
  }
};

export const buyExtraTokens = async (email, packageName, transactionId) => {
  if (!transactionId) {
    throw new Error('transaction_id is required');
  }

  try {
    const payload = {
      email,
      package_name: packageName,
      transaction_id: transactionId,
    };
    const response = await axios.post(URLs.BUY_EXTRA_TOKENS, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to buy extra tokens');
  }
};

export const trackUsage = async (email, feature, incrementBy = 1) => {
  try {
    const response = await axios.post(URLs.TRACK_USAGE, {
      email,
      feature,
      increment_by: incrementBy,
    });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to track usage');
  }
};

export const incrementAnalyticsView = async (commodity) => {
  try {
    if (!commodity) {
      console.warn('incrementAnalyticsView: commodity is required');
      return;
    }
    
    axios.post(URLs.INCREMENT_ANALYTICS_VIEW, { commodity }).catch((error) => {
      console.error('Failed to increment analytics view:', error);
    });
  } catch (error) {
    console.error('Failed to increment analytics view:', error);
  }
};

/**
 * Terminal Analytics layout telemetry (BigQuery). Non-blocking; does not affect usage limits.
 * @param {object} payload
 * @param {string} payload.analytics_view - view1 | view2 | custom
 * @param {string} [payload.email] - fallback if Firebase token has no email
 * @param {'page_load'|'view_change'} [payload.interaction_trigger]
 * @param {string} [payload.source] - e.g. route path
 * @param {boolean} [payload.is_default_hydration]
 * @param {string} [payload.client_event_id] - UUID for dedupe / retries
 */
export const logAnalyticsLayoutUsage = (payload) => {
  try {
    const view = String(payload?.analytics_view || '').toLowerCase();
    if (!['view1', 'view2', 'custom'].includes(view)) {
      return;
    }
    const body = { analytics_view: view };
    if (payload.interaction_trigger) {
      body.interaction_trigger = payload.interaction_trigger;
    }
    if (payload.source != null && payload.source !== '') {
      body.source = String(payload.source);
    }
    if (typeof payload.is_default_hydration === 'boolean') {
      body.is_default_hydration = payload.is_default_hydration;
    }
    if (payload.client_event_id) {
      body.client_event_id = payload.client_event_id;
    }
    if (payload.email) {
      body.email = payload.email;
    }

    axios.post(URLs.LOG_ANALYTICS_LAYOUT_USAGE, body).catch((error) => {
      console.error('Failed to log analytics layout usage:', error);
    });
  } catch (error) {
    console.error('Failed to log analytics layout usage:', error);
  }
};

// Appearance/Theme
export const setAppearance = async (appearance) => {
  try {
    const response = await axios.post(URLs.SET_APPEARANCE, { appearance });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to set appearance');
  }
};

export const setPreferredCommodities = async (commodities) => {
  try {
    const response = await axios.post(URLs.SET_PREFERRED_COMMODITIES, { commodities });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to set preferred commodities');
  }
};

// Tutorial completion
export const setTutorialCompleted = async (completed = true) => {
  try {
    const response = await axios.post(URLs.SET_TUTORIAL_COMPLETED, { completed });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to set tutorial completed');
  }
};

// WhatsApp Linking APIs
export const getWhatsAppStatus = async () => {
  try {
    const response = await axios.get(URLs.GET_WHATSAPP_STATUS);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to get WhatsApp status');
  }
};

export const linkWhatsApp = async (countryCode, nationalNumber) => {
  try {
    const response = await axios.post(URLs.LINK_WHATSAPP, {
      country_code: countryCode,
      national_number: nationalNumber,
    });
    return response.data;
  } catch (error) {
    // Return error response data for error_code handling
    if (error.response?.data) {
      return error.response.data;
    }
    throw new Error(error.response?.data?.message || error.message || 'Failed to link WhatsApp number');
  }
};

export const removeWhatsApp = async () => {
  try {
    const response = await axios.post(URLs.REMOVE_WHATSAPP, {});
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to remove WhatsApp number');
  }
};

// Fire-and-forget welcome email for new signups
export const sendWelcomeEmail = async (email, name) => {
  try {
    const response = await axios.post(URLs.SEND_WELCOME_EMAIL, { email, name });
    return processResponse(response);
  } catch (error) {
    console.warn('Welcome email failed:', error.message);
    return { status: false };
  }
};

export const sendWhatsappWelcome = async () => {
  try {
    const response = await axios.post(URLs.SEND_WHATSAPP_WELCOME);
    return response.data;
  } catch (error) {
    // Silently fail - this is a nice-to-have feature
    console.warn('Failed to send WhatsApp welcome:', error);
    return null;
  }
};

// Notification APIs
export const getNotifications = async (limit = 20, offset = 0) => {
  try {
    const response = await axios.post(URLs.GET_NOTIFICATIONS, { limit, offset });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch notifications');
  }
};

export const markNotificationRead = async (notificationId) => {
  try {
    const response = await axios.post(URLs.MARK_NOTIFICATION_READ, { notification_id: notificationId });
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to mark notification as read');
  }
};

export const markAllNotificationsRead = async () => {
  try {
    const response = await axios.post(URLs.MARK_ALL_NOTIFICATIONS_READ, {});
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to mark all notifications as read');
  }
};

export const getUnreadNotificationCount = async () => {
  try {
    const response = await axios.post(URLs.GET_UNREAD_NOTIFICATION_COUNT, {});
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch unread notification count');
  }
};

// Weather Digest Alerts
export const loadWeatherDigestAlerts = async (commodity, language = 'en') => {
  try {
    const response = await axios.post(URLs.WEATHER_DIGEST_ALERTS, { commodity, language });
    return response.data?.alerts || response.data || [];
  } catch (error) {
    console.error('Failed to load weather digest alerts:', error);
    return [];
  }
};

// Market Insights
export const loadMarketInsights = async (commodities, language = 'en') => {
  try {
    const response = await axios.post(URLs.MARKET_INSIGHTS, {
      commodity: Array.isArray(commodities) ? commodities : [commodities],
      period_type: ['WEEK', 'MONTH', 'TRANSITION'],
      language: language,
      limit: 100,
    });
    return response.data?.insights || [];
  } catch (error) {
    console.error('Failed to load market insights:', error);
    return [];
  }
};

// Sea Tracking
// Chile's source writes port names in Spanish; the UI always shows English.
// Translating here keeps every sea-tracking view on one spelling — see portNames.js.
export const loadActiveShipments = async ({ importer = '', exporter = '' } = {}) => {
  try {
    const params = {};
    if (importer) params.importer = importer;
    if (exporter) params.exporter = exporter;
    const response = await axios.get(URLs.SEA_TRACKING_ACTIVE, { params });
    return withEnglishPorts(processResponse(response));
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message || 'Failed to load shipments');
  }
};

export {
  IMPORTER_SEASON_COMMODITIES,
  IMPORTER_SEASON_KIND,
} from '../constants/importerSeasonPdf.js';

/**
 * Scope tokens are already upper-case (`CANADA--USA__PERU`); trim + upper-case
 * only, so the `__` and `--` separators are never touched.
 */
const normalizeReportDestination = (destination, fallback = 'USA') =>
  String(destination || fallback).trim().toUpperCase() || fallback;

/**
 * Mint a short-lived signed URL for an importer PDF.
 * Default: USA × selected commodity × season_to_date (Option D).
 * Local dev uses the local importer_report server (VITE_IMPORTER_REPORT_BASE_URL).
 */
export const mintImporterReportDownload = async ({
  destination = 'USA',
  commodity = 'grapes',
  kind = 'season_to_date',
  lang = 'en',
} = {}) => {
  const dest = normalizeReportDestination(destination);
  const crop = String(commodity || '').trim().toLowerCase();
  const reportKind = String(kind || 'season_to_date').trim().toLowerCase() || 'season_to_date';
  const pdfLang = normalizePdfLang(lang);

  try {
    const response = await axios.get(URLs.IMPORTER_REPORT_DOWNLOAD, {
      params: { destination: dest, commodity: crop, kind: reportKind, lang: pdfLang },
    });
    const body = response?.data;
    if (!body?.status || !body?.url) {
      throw new Error(body?.error || 'Report not available');
    }
    return body;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || error.message || 'Report not available',
    );
  }
};

/** Preview sidecar for next10 View-all or season_to_date Season Summary.
 * season_to_date returns season_access.
 * A season_to_date cell with no pre-generated pack comes back `live: true`; with
 * no volume `empty: true` and `preview: null`.
 */
export const loadImporterReportPreview = async ({
  destination = 'USA',
  commodity = 'grapes',
  kind = 'next10',
  signal,
} = {}) => {
  const dest = normalizeReportDestination(destination);
  const crop = String(commodity || '').trim().toLowerCase();
  const reportKind = String(kind || 'next10').trim().toLowerCase() || 'next10';

  try {
    const response = await axios.get(URLs.IMPORTER_REPORT_PREVIEW, {
      params: { destination: dest, commodity: crop, kind: reportKind },
      signal,
    });
    const body = response?.data;
    if (!body?.status) {
      throw new Error(body?.error || 'Report not available');
    }
    return body;
  } catch (error) {
    if (axios.isCancel?.(error)) throw error;
    const err = new Error(
      error.response?.data?.error || error.message || 'Report not available',
    );
    err.status = error.response?.status;
    throw err;
  }
};

/**
 * Season Summary bundle for the map cards and modal (independent of sea
 * tracking). Resolves to {status, season_access, available_destinations, packs}.
 */
export const loadImporterSeasonBundle = async () => {
  try {
    const response = await axios.get(URLs.IMPORTER_REPORT_PREVIEW, {
      params: { bundle: 'season' },
    });
    const body = response?.data;
    if (!body || typeof body !== 'object' || body.status === false) {
      throw new Error(body?.error || 'Season summary not available');
    }
    return body;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || error.message || 'Season summary not available',
    );
  }
};

export const loadShipmentHistory = async (containerNumber) => {
  try {
    const response = await axios.get(`${URLs.SEA_TRACKING_HISTORY}?container=${encodeURIComponent(containerNumber)}`);
    return withEnglishPorts(processResponse(response));
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message || 'Failed to load shipment history');
  }
};

export const loadCarriers = async () => {
  try {
    const response = await axios.get(URLs.SEA_TRACKING_CARRIERS);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message || 'Failed to load carriers');
  }
};

export const searchContainer = async (query) => {
  try {
    const response = await axios.get(`${URLs.SEA_TRACKING_CONTAINER_SEARCH}?q=${encodeURIComponent(query)}`);
    return withEnglishPorts(processResponse(response));
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message || 'Failed to search container');
  }
};

// Air and truck. Both read their own BigQuery view; port names get the same
// English display mapping as sea.
export const loadActiveFlights = async () => {
  try {
    const response = await axios.get(URLs.AIR_TRACKING_ACTIVE);
    return withEnglishPorts(processResponse(response));
  } catch (error) {
    const err = new Error(error.response?.data?.error || error.message || 'Failed to load flights');
    err.status = error.response?.status;
    throw err;
  }
};

export const loadActiveTrucks = async () => {
  try {
    const response = await axios.get(URLs.TRUCK_TRACKING_ACTIVE);
    return withEnglishPorts(processResponse(response));
  } catch (error) {
    const err = new Error(error.response?.data?.error || error.message || 'Failed to load trucks');
    err.status = error.response?.status;
    throw err;
  }
};

// =============================================================================
// Cargo Shipments (Peru) API
// =============================================================================

/**
 * Fetch Peru cargo varieties data (butterfly chart — East/West Coast split)
 * Returns all 3 time periods (3d, 7d, 10d) in a single call.
 * @param {string} email - User email
 * @param {string} commodity - Commodity name (e.g. "grapes")
 * @param {string|null} dateEnd - Optional end date (YYYY-MM-DD)
 * @param {number|null} topN - Optional max varieties before "Other Varieties" bucket
 */
export const loadCargoVarieties = async (commodity, dateEnd = null, topN = null) => {
  try {
    // Backend derives user email from Firebase token; do not send `email` from the client.
    const payload = { commodity };
    if (dateEnd) payload.date_end = dateEnd;
    if (topN) payload.top_n = topN;
    const response = await axios.post(URLs.CARGO_SHIPMENTS, payload);
    return processResponse(response);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to load cargo varieties');
  }
};

// =============================================================================
// Procurement Planner
// =============================================================================

/**
 * Cherry procurement metrics (volume, FOB cost, signals, holidays).
 * @param {string[]|null} regions - Optional region filter (display names, e.g. "Chile").
 */
export const loadProcurementMetrics = async (regions = null) => {
  try {
    const payload = {};
    if (regions?.length) payload.regions = regions;

    const response = await axios.post(URLs.PROCUREMENT_METRICS, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return processResponse(response);
  } catch (error) {
    const status = error.response?.status;
    const apiMessage = error.response?.data?.message;
    if (status === 401) {
      throw new Error(apiMessage || 'Sign in required to load procurement data.');
    }
    if (status === 404) {
      throw new Error(
        apiMessage ||
          'Procurement metrics endpoint is not available on this environment.',
      );
    }
    throw new Error(apiMessage || error.message || 'Failed to load procurement metrics');
  }
};

const handleProcurementGrowersError = async (error, retryFn) => {
  const status = error.response?.status;
  const apiMessage = error.response?.data?.message;
  if (status === 401 && retryFn) {
    const auth = getAuth(authApp);
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true);
      return retryFn();
    }
  }
  if (status === 401) {
    throw new Error(apiMessage || 'Sign in required to load grower data.');
  }
  if (status === 400) {
    throw new Error(apiMessage || 'Invalid grower data.');
  }
  if (status === 404) {
    throw new Error(apiMessage || 'Grower endpoints are not available on this environment.');
  }
  throw new Error(apiMessage || error.message || 'Grower request failed');
};

/**
 * GET procurement grower programs for the signed-in user.
 * @returns {Promise<{ status: boolean, growers: object[], lastUpdated: string|null }>}
 */
export const fetchProcurementGrowers = async () => {
  const request = async () => {
    const response = await axios.get(URLs.PROCUREMENT_GET_GROWERS);
    const body = response.data;
    if (body?.status === false) {
      throw new Error(body.message || 'Failed to load growers');
    }
    return body;
  };
  try {
    return await request();
  } catch (error) {
    return handleProcurementGrowersError(error, request);
  }
};

/**
 * POST full grower list (all regions). Replaces server state.
 * @param {object[]} growers
 */
export const saveProcurementGrowers = async (growers) => {
  const request = async () => {
    const response = await axios.post(URLs.PROCUREMENT_SAVE_GROWERS, { growers });
    const body = response.data;
    if (body?.status === false) {
      throw new Error(body.message || 'Failed to save growers');
    }
    return body;
  };
  try {
    return await request();
  } catch (error) {
    return handleProcurementGrowersError(error, request);
  }
};

/**
 * Remove a grower or a single ISO week from a grower (v2).
 * @param {{ id: string, week?: number }} body
 */
export const removeProcurementGrower = async (body) => {
  const request = async () => {
    const response = await axios.post(URLs.PROCUREMENT_REMOVE_GROWER, body);
    const data = response.data;
    if (data?.status === false) {
      throw new Error(data.message || 'Remove failed');
    }
    return data;
  };
  try {
    return await request();
  } catch (error) {
    return handleProcurementGrowersError(error, request);
  }
};

// Extreme Weather Alerts
export const loadExtremeWeatherAlerts = async (commodity, language = 'en', status = 'all') => {
  try {
    const response = await axios.post(URLs.EXTREME_WEATHER_ALERTS, {
      commodity: commodity,
      language: language,
      status: status
    });
    return response.data?.alerts || response.data || [];
  } catch (error) {
    console.error('Failed to load extreme weather alerts:', error);
    return [];
  }
};
